import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import {
  ApiError, api, deviceMe, enrollDevice, renameDevice,
  type Enrolled, type Me,
} from '../api'
import { Field } from '../components/bits'
import { useT } from '../lang'

/* Getting a device in.
 *
 * The person on the other side of this screen is standing on a walkway in wet
 * gloves. They have a card with twelve characters on it, or a QR their manager
 * held up. Everything here is built for that: one field, one button, no
 * account, no address, no password, and nothing to remember afterwards.
 *
 * It renders from `Gate` BEFORE the session check, like the reset pages, and
 * for the same reason -- a device with no session would otherwise be shown a
 * sign-in form it can never get past. Unlike them it also renders WITH a
 * session, because "Bytt kode" is how a tablet moves from one farm to the whole
 * area, and that is a POST with the cookie still attached. Signing out first
 * would mint a second device and strand a month of counts on the first.
 */

/** Fold what somebody typed into what they meant.
 *
 *  The same rule the receiver applies, deliberately duplicated rather than
 *  round-tripped: the field should show `K7NP-3RQX-9TFD` as it is typed, and
 *  it cannot do that by asking a server. I and O are not in the alphabet
 *  precisely so they can be folded here -- a character that cannot be part of
 *  a real code can only be a misreading of the digit beside it on the label.
 *
 *  The server canonicalises again on arrival and is the authority; this exists
 *  so the field does not fight the person using it.
 */
export function canonical(raw: string): string {
  return raw
    .toUpperCase()
    .replace(/I/g, '1')
    .replace(/O/g, '0')
    .replace(/[^0-9A-Z]/g, '')
    .slice(0, 16)
}

/** The shortest thing that could be a code. Three, matching the server. */
const MIN_CODE = 3

/** Grouped in fours, but only when there is something to group.
 *
 *  Twelve characters in one run is a transcription error waiting to happen.
 *  Four are not, and "LJ-02" is harder to read aloud than "LJ02" -- so the
 *  grouping starts where the problem it solves starts. Must match display()
 *  in app/devices.py, or a manager reads one thing off the office screen and
 *  types another on the phone.
 */
export function grouped(code: string): string {
  if (code.length <= 8) return code
  return code.replace(/(.{4})(?=.)/g, '$1-')
}

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="login-wrap">
      <div className="login">
        <div className="wordmark">
          <span className="dot" aria-hidden="true" />
          Phoniq
        </div>
        {children}
      </div>
    </div>
  )
}

function message(t: (k: string) => string, err: unknown): string {
  if (!(err instanceof ApiError)) return t('Could not reach the receiver.')
  switch (err.code) {
    /* Deliberately one message for unknown, expired, revoked and used up. The
       server answers identically for all four -- telling them apart would make
       this endpoint a way to discover which codes exist -- so the screen must
       not invent a distinction the receiver refused to make. */
    case 'code_unknown':
      return t('That code is not in use. Check it, or ask for a new one.')
    case 'code_scope_gone':
      return t('That code points at something that is no longer there.')
    case 'too_many_attempts':
      return t('Too many tries from here. Wait a few minutes.')
    case 'invalid_request':
      return t('A code is twelve characters.')
    default:
      return err.detail ?? err.code
  }
}

/** Read the code out of the fragment once, then take it off the address bar.
 *
 *  A fragment is never sent to a server, so the code cannot reach an access
 *  log or a Referer header, and a corporate link scanner that fetches every URL
 *  in an inbound message cannot spend it. It IS visible in the address bar and
 *  kept in history, which is exactly why the code that travels this way is the
 *  single-use, short-lived one. Stripped immediately so a screenshot or a
 *  shoulder does not carry it either.
 */
function useHashCode(): string {
  const [code] = useState(() => canonical(window.location.hash.replace(/^#/, '')))
  useEffect(() => {
    if (window.location.hash) {
      history.replaceState(null, '', window.location.pathname)
    }
  }, [])
  return code
}

export function Koble({ onEnrolled }: { onEnrolled: (me: Me) => void }) {
  const t = useT()
  const scanned = useHashCode()
  const [code, setCode] = useState(scanned)
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [waiting, setWaiting] = useState<Enrolled | null>(null)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!ready) return
    setBusy(true)
    setError(null)
    try {
      const r = await enrollDevice(code, name.trim() || undefined)
      if (!r.approved) {
        // Nothing was granted. The screen stops here and watches, rather than
        // dropping into a shell with no farms in it and letting the worker
        // conclude the code was wrong.
        setWaiting(r)
        setBusy(false)
        return
      }
      // Identity is read back from the server rather than assembled from the
      // enrollment response, so what the shell renders comes from one place.
      onEnrolled(await api.get<Me>('/v1/me'))
    } catch (err) {
      setError(message(t, err))
      setBusy(false)
    }
  }

  if (waiting) {
    return <Waiting first={waiting} onApproved={async () =>
      onEnrolled(await api.get<Me>('/v1/me'))} />
  }

  /* A NAME IS ASKED FOR BEFORE THE CODE IS SPENT, not after.
   *
   * It used to be the other way round, and a scanned QR submitted itself on
   * sight -- correct while the code was single-use and secret, because there
   * was nothing to confirm and every second on that screen was a second the
   * code sat in an address bar. Neither is true now: the code is an address, a
   * crew shares it, and scanning it twice costs nothing.
   *
   * What changed the order is the notification. It is sent when the code is
   * exchanged, so a name typed afterwards arrives too late to be in it -- and
   * the manager deciding whether to let this device in would be reading "en
   * enhet har bedt om å koble seg til" about a phone they cannot identify.
   * Asking first is what makes that message answerable.
   */
  const ready = code.length >= MIN_CODE && name.trim().length > 0

  return (
    <Frame>
      <form className="card stack" onSubmit={submit} style={{ gap: 18 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20 }}>{t('Connect this device')}</h2>
          <p className="muted" style={{ margin: '6px 0 0', fontSize: 14 }}>
            {t('Type the code you were given. There is nothing else to set up.')}
          </p>
        </div>

        <Field label="Code"
          hint={t('Upper and lower case are the same, and dashes do not matter.')}>
          <input
            value={grouped(code)}
            onChange={(e) => setCode(canonical(e.target.value))}
            /* Not `type="password"`. A code read off a card by somebody in
               gloves has to be visible to be corrected, and it admits a device
               to one farm rather than an account to a company. */
            inputMode="text"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            autoComplete="off"
            autoFocus={!scanned}
            required
            style={{
              fontSize: 24, letterSpacing: '0.14em', textAlign: 'center',
              fontFamily: 'var(--mono, ui-monospace, monospace)',
            }}
          />
        </Field>

        <Field label="Name"
          hint={t('So your counts can be told apart from the next tablet’s, and so your manager knows what they are letting in.')}>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            autoFocus={!!scanned}
            required
            placeholder={t('Merd 3, or your name')}
          />
        </Field>

        {error && <div className="notice bad">{error}</div>}

        <button className="btn primary" type="submit" disabled={busy || !ready}>
          {busy ? t('Connecting…') : t('Connect')}
        </button>

        {/* Said before it happens rather than discovered afterwards. */}
        <p className="faint" style={{ margin: 0, fontSize: 12 }}>
          {t('This device stays connected. You will not be asked again.')}
        </p>
      </form>
    </Frame>
  )
}

/* -------------------------------------------------------- the waiting room */

/** What a worker sees while a manager decides.
 *
 *  This screen exists because the alternative is worse in a specific way: a
 *  device that enrolled but was not admitted has a session and no farms, so
 *  dropping it into the ordinary shell would show an empty list — and somebody
 *  standing on a walkway reads an empty list as "I typed the code wrong" and
 *  types it again. Naming the actual state is the whole job.
 *
 *  It polls rather than waits for a push. There is no channel to push down, the
 *  answer usually arrives within a minute of somebody's phone buzzing, and a
 *  poll that stops when the tab is hidden costs a request every few seconds
 *  from a device that is otherwise doing nothing.
 */
function Waiting({
  first, onApproved,
}: { first: Enrolled; onApproved: () => Promise<void> }) {
  const t = useT()
  const [dots, setDots] = useState(0)

  useEffect(() => {
    let live = true
    let timer: number | undefined

    const tick = async () => {
      if (!live) return
      try {
        const me = await deviceMe()
        if (live && me.approved && !me.waiting) {
          await onApproved()
          return
        }
      } catch {
        /* A declined device answers 401 here, and Gate's own unauthorised
           handler puts it back on the connect screen. Nothing to do but keep
           asking until that happens. */
      }
      if (live) {
        setDots((d) => (d + 1) % 4)
        timer = window.setTimeout(tick, 4000)
      }
    }

    void tick()
    return () => { live = false; window.clearTimeout(timer) }
  }, [])

  return (
    <Frame>
      <div className="card stack" style={{ gap: 18 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20 }}>
            {t('Waiting to be let in')}{'.'.repeat(dots)}
          </h2>
          <p className="muted" style={{ margin: '8px 0 0', fontSize: 14 }}>
            {t('Your manager has been asked to approve this device for')}{' '}
            <strong>{first.scope}</strong>. {t('It usually takes a moment.')}
          </p>
        </div>

        <div className="notice">
          {t('You can leave this screen open. It will start on its own.')}
        </div>

        <p className="faint" style={{ margin: 0, fontSize: 12 }}>
          {t('Nothing is registered and nothing is visible until then.')}
        </p>
      </div>
    </Frame>
  )
}

/* ----------------------------------------------------------------- the name */

/* "Hvem er du?" is now asked on the connect form itself, before the code is
 * exchanged, so that the approval request a manager receives names the device.
 * What remains here is the form on its own, which the shell still hosts for a
 * device that got in without a name -- an older enrollment, or a browser
 * reloaded mid-answer.
 */
/** The form itself, without the frame, so the shell can host it too.
 *
 *  A device that was connected without a name — a QR scanned in a hurry, a
 *  browser reloaded mid-answer — reaches this from `Gate` on its next visit
 *  rather than never being asked again.
 */
export function NameForm({
  lead, submitLabel, onNamed,
}: { lead?: string; submitLabel?: string; onNamed: () => Promise<void> }) {
  const t = useT()
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await renameDevice(name.trim())
      await onNamed()
    } catch (err) {
      setError(err instanceof ApiError ? (err.detail ?? err.code)
        : t('Could not reach the receiver.'))
      setBusy(false)
    }
  }

  return (
    <form className="card stack" onSubmit={submit} style={{ gap: 18 }}>
      <div>
        <h2 style={{ margin: 0, fontSize: 20 }}>{t('What is this device called?')}</h2>
        {lead && (
          <p className="muted" style={{ margin: '6px 0 0', fontSize: 14 }}>{lead}</p>
        )}
      </div>

      <Field label="Name"
        hint={t('So your counts can be told apart from the next tablet’s. Change it any time.')}>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={200}
          autoFocus
          required
          placeholder={t('Merd 3, or your name')}
        />
      </Field>

      {error && <div className="notice bad">{error}</div>}

      <button className="btn primary" type="submit" disabled={busy || !name.trim()}>
        {busy ? t('Saving…') : (submitLabel ?? t('Start counting'))}
      </button>
    </form>
  )
}
